import {Dispatch, SetStateAction, useEffect} from "react";
import {Pagination} from "react-bootstrap";
import {ChevronLeft, ChevronRight} from 'react-bootstrap-icons';
import {useTranslation} from "react-i18next";
import './Pager.scss'

interface PagerProps {
    itemCount : number
    currentPage : number
    setCurrentPage? : Dispatch<SetStateAction<number>>
    maxItemsPerPage : number
}

export const Pager = ({itemCount, currentPage, setCurrentPage, maxItemsPerPage} : PagerProps) =>
{
    // there should always be one page even if there are no items
    const nrOfPages = Math.max(1, Math.ceil(itemCount / maxItemsPerPage));

    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    useEffect(() =>
    {
        // can happen that items were deleted and that there are now less pages
        if (setCurrentPage !== undefined && currentPage > nrOfPages) {
            setCurrentPage(nrOfPages);
        }
    }, [currentPage, nrOfPages, setCurrentPage]);

    const onChangePage = (newPage: number) =>
    {
        // if page nr is invalid, do nothing
        if (newPage < 1 || newPage > nrOfPages) {
            return
        }

        if (setCurrentPage !== undefined) {
            setCurrentPage(newPage);
        }
    }

    function getCurrentPage() : number{
        return  currentPage > nrOfPages ? nrOfPages : currentPage;
    }

    function renderPages()
    {
        if (nrOfPages <= 5) {
            return [...Array(nrOfPages).keys()].map(i => <Pagination.Item key={i+1} active={getCurrentPage() === i+1} onClick={() => onChangePage(i+1)}>{i+1}</Pagination.Item>)
        }
        else {
            return(
                <>
                {/*show on larger devices*/}
                <div className="d-flex d-none d-md-inline-flex">
                    <Pagination.Item key={1} active={getCurrentPage() === 1} onClick={() => onChangePage(1)}>1</Pagination.Item>
                    <Pagination.Item key={2} active={getCurrentPage() === 2} onClick={() => onChangePage(2)}>2</Pagination.Item>
                    {currentPage === 3 &&
                        <Pagination.Item key={3} active={true} onClick={() => onChangePage(getCurrentPage())}>{getCurrentPage()}</Pagination.Item>
                    }
                    {currentPage > 3 &&
                        <>
                            <Pagination.Ellipsis key={4} disabled={true}/>
                            <Pagination.Item key={5} active={true} onClick={() => onChangePage(getCurrentPage())}>{getCurrentPage()}</Pagination.Item>
                        </>
                    }

                    {getCurrentPage() < (nrOfPages - 2) &&
                        <Pagination.Ellipsis key={6} disabled={true}/>
                    }

                    {getCurrentPage() < (nrOfPages -1) &&
                        <Pagination.Item key={7} active={false} onClick={() => onChangePage(nrOfPages - 1)}>{nrOfPages - 1}</Pagination.Item>
                    }
                    {getCurrentPage() < nrOfPages &&
                        <Pagination.Item key={8} active={false} onClick={() => onChangePage(nrOfPages)}>{nrOfPages}</Pagination.Item>
                    }
                </div>

                {/*on smaller devices only show the current page*/}
                <div className="d-none d-sm-inline-flex d-md-none">
                    <Pagination.Item key={1} active={true} onClick={() => onChangePage(getCurrentPage())}>{getCurrentPage()}</Pagination.Item>
                </div>
            </>
            )
        }
    }

    return (
        <Pagination>
            <Pagination.Prev onClick={() => onChangePage(getCurrentPage() - 1)} disabled={getCurrentPage() === 1} className="d-flex align-items-center"> <ChevronLeft/> {t("pager.previous")} </Pagination.Prev>

            {renderPages()}

            <Pagination.Prev onClick={() => onChangePage(getCurrentPage() + 1)} disabled={getCurrentPage() === nrOfPages} className="d-flex align-items-center"> {t("pager.next")} <ChevronRight/> </Pagination.Prev>
        </Pagination>
    )
}
